// openInSlot — the ONE "open this href in the shell PageSlot, else navigate"
// decision, extracted so NotificationRow (Notifications.js — the bell
// dropdown panel AND /inbox), the toast click handler (GlobalNotification.js),
// and the global search dropdown (workspace/functions/search/globalSearch.js
// navigateSearchResult) can never drift into three slightly-different
// implementations (2026-08-02 per Nika "notifications/searchdropdown open in
// pageslot not page").
//
// Lives HERE, not in workspace/functions, for the same reason
// markNotificationRead does (see that file's header): canvas's standalone
// bundle links shared but never workspace, and NotificationRow renders there
// too (canvas's own dashboard PageSlot instance) — an
// el.call('openInSlot', …) fired from that context must resolve here.
//
// Route-support check mirrors the EXACT resolution chain
// canvas/components/PageSlot.js's `_defFor` uses (documented in
// workspace/state-app.js's `_canvasSlot` comment: "_defFor → root._slotResolve
// → context.pages") so this can never open a route the slot itself would
// render as an empty sheet, and never guesses at an allowlist that could
// drift from the real mount.
//
// Tickets sub-routes opened from OUTSIDE the tickets app are a deliberate,
// Nika-directed exception (fix ba958f95 "open tickets search results
// in-canvas, not the CanvasPageSlot overlay"): /tickets owns a full-bleed
// pan/zoom canvas, so a subview like /tickets/my floating as an overlay over
// an unrelated page (e.g. /studio) reads wrong. External openers — this
// function — always take the full route into /tickets. TicketsSidebar's OWN
// internal slot navigation (tickets/components/Sidebar.js) is unaffected: it
// writes root._canvasSlot directly and never routes through this function.
import { cleanHref } from './minimizedSlots.js'
import { slotPaneKeyFor } from './slotPane.js'

// FA204 / frank trap — _forceNavigate/_hasSlotDef/_navigate used to live at
// module scope and get referenced bare from the two exports below; each
// export now carries its own self-contained copy (see appAccess.js for the
// identical "shared by 2 exports, duplicated into both" shape).

// el.call('canOpenInSlot', href, el) — TRUE when openInSlot would mount
// `href` in the shell PageSlot (a slot def exists and the route isn't in the
// force-navigate set). Lets slot-first entry points (NotificationBell) pick
// their UI without duplicating the resolution chain above — the predicate and
// the action can never disagree because they share the same forceNavigate/
// hasSlotDef logic.
export const canOpenInSlot = function (href, el) {
  const forceNavigate = (h) =>
    /^\/(tickets|tasks)(\/|\?|#|$)/.test(h || '') || /^\/task\//.test(h || '')
  const hasSlotDef = (e, root, clean) =>
    !!(
      (root && root._aiAnswerPages && root._aiAnswerPages[clean]) ||
      (root && typeof root._slotResolve === 'function' && root._slotResolve(clean)) ||
      e?.context?.pages?.[clean]
    )

  const target = el || this
  const root = target?.state?.root
  if (!href || !target || !root) return false
  if (forceNavigate(href)) return false
  return hasSlotDef(target, root, href.split('?')[0])
}

// el.call('openInSlot', href, el) — open `href` in the shell PageSlot
// (root._canvasSlot) when its route has a mountable PageSlot page def;
// otherwise fall back to a normal full-page navigation so a click never
// silently does nothing.
export const openInSlot = function (href, el) {
  const forceNavigate = (h) =>
    /^\/(tickets|tasks)(\/|\?|#|$)/.test(h || '') || /^\/task\//.test(h || '')
  const hasSlotDef = (e, root, clean) =>
    !!(
      (root && root._aiAnswerPages && root._aiAnswerPages[clean]) ||
      (root && typeof root._slotResolve === 'function' && root._slotResolve(clean)) ||
      e?.context?.pages?.[clean]
    )
  // Walk up from `el` to the nearest routes-bearing ancestor and drive a
  // real SPA navigation — the same idiom every el.router() call site in this
  // codebase uses (NotificationRow, pageContext.js's routeEl(), chat's
  // data-ref-* handlers) rather than el.getRoot(), which doesn't reliably
  // carry `.routes` from inside a portal-rendered layer (dropdown / toast
  // stack).
  const navigate = (e, h) => {
    let rootEl = e
    while (rootEl && !rootEl.routes) rootEl = rootEl.parent
    if (rootEl && typeof e.router === 'function') e.router(h, rootEl)
  }

  const target = el || this
  const root = target?.state?.root
  if (!href || !target || !root || typeof root.update !== 'function') return
  if (forceNavigate(href)) {
    navigate(target, href)
    return
  }
  const clean = href.split('?')[0]
  if (hasSlotDef(target, root, clean)) {
    // PANE-SCOPED targeting (split-view fix, 2026-08-06 per Nika "fix bug
    // when its on split view - it often changes both views on navigation
    // click"): an open fired from INSIDE a slot pane targets that pane's own
    // state key (slotPaneKeyFor walks to the pane/host stamp PageSlot lays
    // down) — a second-pane interaction must never retarget the primary. And
    // a route already living in the second pane re-focuses/deep-links THERE
    // instead of duplicating into the primary — the iPad "tap an app that's
    // already in the split" semantics.
    //
    // A SPLIT INTENT (the pane header's split button armed it: the NEXT pick
    // lands beside the primary) wins: the pick opens in the SECOND pane — in
    // the primary only when none is open — and the intent is spent in the
    // SAME update, so no frame shows the pick landed while the split still
    // reads "arming", and a later pick never lands beside it again.
    if (root._slotSplitIntent) {
      root.update({
        [root._canvasSlot ? '_canvasSlotSecond' : '_canvasSlot']: href,
        _slotSplitIntent: false
      })
      return
    }
    let paneKey = slotPaneKeyFor(target)
    if (
      paneKey === '_canvasSlot' &&
      root._canvasSlotSecond &&
      cleanHref(root._canvasSlotSecond) === clean
    ) {
      paneKey = '_canvasSlotSecond'
    }
    root.update({ [paneKey]: href })
    return
  }
  navigate(target, href)
}
